/**
 * Authentication Context
 *
 * Provides Firebase authentication state and methods throughout the app.
 * Supports:
 * - Email/Password authentication
 * - OAuth providers (Google, Apple, Microsoft, Yahoo)
 * - Multi-Factor Authentication (MFA) with TOTP or SMS
 * - Session management
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  sendEmailVerification,
  multiFactor,
  PhoneMultiFactorGenerator,
  PhoneAuthProvider,
  RecaptchaVerifier,
  getIdToken,
  getMultiFactorResolver,
  reload,
} from 'firebase/auth';
import type { MultiFactorResolver, MultiFactorError, User } from 'firebase/auth';
import {
  auth,
  googleProvider,
  appleProvider,
  // microsoftProvider, // Removed - will be re-added if Windows PC app is created
  // yahooProvider, // Removed - not needed for current strategy
  signInWithProvider,
  handleAuthRedirect,
} from '../config/firebase';
import type { OAuthProviderType } from '../config/firebase';
import analyticsService from '../services/analytics';

// =============================================================================
// Types
// =============================================================================

interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  mfaEnabled: boolean;
  enrolledFactors: string[];
}

interface MFAState {
  required: boolean;
  resolver: MultiFactorResolver | null;
  verificationId: string | null;
  enrolling: boolean;
}

interface AuthContextType {
  // User state
  user: AuthUser | null;
  loading: boolean;
  error: string | null;

  // MFA state
  mfaState: MFAState;

  // Email/Password methods
  signUp: (
    email: string,
    password: string,
    displayName: string,
    turnstileToken?: string,
  ) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendVerificationEmail: () => Promise<void>;
  /**
   * Re-read the signed-in user from Firebase and republish the snapshot.
   * `emailVerified` lives in the cached ID token, so it only changes when
   * something calls `reload()` - `onAuthStateChanged` does NOT fire for it.
   */
  refreshUser: () => Promise<void>;

  // OAuth methods
  signInWithOAuth: (provider: OAuthProviderType) => Promise<void>;

  // MFA methods
  startMFAEnrollment: (phoneNumber: string) => Promise<string>;
  completeMFAEnrollment: (verificationCode: string, displayName?: string) => Promise<void>;
  verifyMFACode: (verificationCode: string) => Promise<void>;
  cancelMFA: () => void;

  // TOTP MFA methods
  checkMFARequired: (email: string, password: string) => Promise<boolean>;
  verifyTOTPCode: (email: string, password: string, code: string) => Promise<void>;

  // Session methods
  signOutUser: () => Promise<void>;
  getAuthToken: () => Promise<string | null>;

  // Utility
  clearError: () => void;
}

// =============================================================================
// Context
// =============================================================================

// Single API base for this module. The codebase has two conventions for the
// VITE_API_URL fallback - '' (same origin) and the Railway host - and this
// file briefly carried BOTH, 48 lines apart.
//
// The Railway fallback is the safe one. With '', a build made without
// VITE_API_URL resolves /api/v1/auth/me same-origin, 404s, and silently pins
// every user's MFA badge to "Not Set" - reintroducing through a different
// door the exact frozen-false bug this read exists to fix, visible only as a
// console.error.
//
// Deliberately NOT `getApiBaseUrl()` from utils/platform: that returns the
// string '/api/v1' on web and would double the prefix for callers here.
const API_BASE =
  import.meta.env.VITE_API_URL ||
  'https://claude-essay-agent-production.up.railway.app';

/**
 * Read the authoritative MFA state from the backend.
 *
 * WHY NOT FIREBASE `enrolledFactors`
 * ----------------------------------
 * This app enrols MFA through its own `/api/v1/mfa` routes, which write
 * `users.mfa_enabled` in Postgres. That never creates a Firebase second
 * factor, and project-level Firebase MFA is disabled besides, so
 * `multiFactor(user).enrolledFactors` is empty for every user and always will
 * be. Deriving `mfaEnabled` from it froze every badge, prompt and analytics
 * property at false no matter what the user did.
 *
 * `GET /api/v1/auth/me` returns `users.mfa_enabled` directly and is exempt
 * from MFA enforcement, so it stays reachable while a user is blocked.
 *
 * Returns null when the value could not be read, so callers can distinguish
 * "not enabled" from "unknown" and leave the displayed state alone.
 */
async function fetchBackendMfaEnabled(firebaseUser: User): Promise<boolean | null> {
  try {
    const token = await getIdToken(firebaseUser);
    const response = await fetch(`${API_BASE}/api/v1/auth/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) return null;
    const data = await response.json();
    return typeof data?.mfa_enabled === 'boolean' ? data.mfa_enabled : null;
  } catch (err) {
    // Never let this break sign-in - an unknown MFA badge is recoverable,
    // a failed auth state transition is not.
    console.error('Could not read MFA status from backend:', err);
    return null;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =============================================================================
// Provider Component
// =============================================================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // API base URL with fallback

  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mfaState, setMfaState] = useState<MFAState>({
    required: false,
    resolver: null,
    verificationId: null,
    enrolling: false,
  });

  // Recaptcha verifier for phone auth
  const [recaptchaVerifier, setRecaptchaVerifier] = useState<RecaptchaVerifier | null>(null);

  // ---------------------------------------------------------------------------
  // Auth State Listener
  // ---------------------------------------------------------------------------

  useEffect(() => {
    // Handle OAuth redirect result (for mobile native apps)
    // This must run before we start listening for auth state changes
    const checkRedirectResult = async () => {
      try {
        const result = await handleAuthRedirect();
        if (result?.user) {
          console.log('Auth redirect completed for:', result.user.email);
          // Map provider ID to our provider type for analytics
          const providerMap: Record<string, OAuthProviderType> = {
            'google.com': 'google',
            'apple.com': 'apple',
            // 'microsoft.com': 'microsoft', // Removed
            // 'yahoo.com': 'yahoo', // Removed
          };
          const providerType = providerMap[result.providerId || ''] || 'google';
          analyticsService.trackLoginComplete(providerType, result.user.uid);
        }
      } catch (err) {
        // Handle redirect errors
        const message = handleAuthError(err);
        setError(message);
        analyticsService.trackAuthError('oauth_redirect', message);
      }
    };

    // Check for redirect result first
    checkRedirectResult();

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Pinned now, because everything below this line runs after an await
        // and `firebaseUser` may no longer be the signed-in account by then.
        const uid = firebaseUser.uid;

        // Kept for callers that genuinely want Firebase factors. This is NOT
        // the app's MFA state - see fetchBackendMfaEnabled above for why it is
        // expected to stay empty.
        const mfaUser = multiFactor(firebaseUser);
        const enrolledFactors = mfaUser.enrolledFactors.map((f) => f.factorId);

        // Paint with the pessimistic value and correct it from the backend
        // below, rather than awaiting the network before the app renders.
        // Going false -> true is a harmless upgrade; the reverse would briefly
        // tell a protected user they are unprotected.
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          emailVerified: firebaseUser.emailVerified,
          mfaEnabled: false,
          enrolledFactors,
        });
        setLoading(false);

        // Identify without asserting an MFA value we have not read yet.
        analyticsService.setUser(uid, {
          email: firebaseUser.email || undefined,
        });

        const backendMfaEnabled = await fetchBackendMfaEnabled(firebaseUser);

        // This callback is async and Firebase neither serialises nor awaits
        // it, so a slow /auth/me for one account can resolve AFTER a
        // different account has signed in. Applying the result blindly writes
        // one user's MFA state onto another - and in the dangerous direction:
        // an unprotected user told they are protected, with nothing to
        // correct it until the next auth transition.
        //
        // Checking `prev` for null is not enough; that only catches sign-out,
        // not an identity change. Both writes below are gated on the account
        // still being the one we read for.
        if (backendMfaEnabled !== null) {
          setUser((prev) =>
            prev && prev.uid === uid
              ? { ...prev, mfaEnabled: backendMfaEnabled }
              : prev
          );

          // Re-identifying analytics as a previous account would attribute
          // the new user's entire session to it.
          if (auth.currentUser?.uid === uid) {
            analyticsService.setUser(uid, {
              email: firebaseUser.email || undefined,
              mfaEnabled: backendMfaEnabled,
            });
          }
        }
      } else {
        setUser(null);
        analyticsService.clearUser();
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // ---------------------------------------------------------------------------
  // Error Handling
  // ---------------------------------------------------------------------------

  const handleAuthError = (err: unknown): string => {
    if (err instanceof Error) {
      const message = err.message;

      // Backend-specific error messages
      if (message.includes('disposable email')) {
        return 'Disposable email addresses are not allowed. Please use a permanent email.';
      }
      if (message.includes('Too many signups') || message.includes('rate limit')) {
        return 'Too many accounts created from your location. Please try again later.';
      }
      if (message.includes('already exists') || message.includes('duplicate')) {
        return 'An account with this email already exists. Try signing in instead.';
      }
      if (message.includes('Account creation failed')) {
        return 'Account creation failed. Please try again or contact support.';
      }

      // Check for MFA required error
      if ((err as MultiFactorError).code === 'auth/multi-factor-auth-required') {
        const mfaError = err as MultiFactorError;
        const resolver = getMultiFactorResolver(auth, mfaError);
        setMfaState({
          required: true,
          resolver: resolver,
          verificationId: null,
          enrolling: false,
        });
        return 'Please complete two-factor authentication.';
      }

      // Map Firebase error codes to user-friendly messages
      const errorMessages: Record<string, string> = {
        'auth/email-already-in-use': 'This email is already registered. Please sign in instead.',
        'auth/invalid-email': 'Please enter a valid email address.',
        'auth/operation-not-allowed': 'This sign-in method is not enabled.',
        'auth/weak-password': 'Password should be at least 6 characters.',
        'auth/user-disabled': 'This account has been disabled.',
        'auth/user-not-found': 'No account found with this email.',
        'auth/wrong-password': 'Incorrect password. Please try again.',
        'auth/invalid-credential': 'Invalid credentials. Please check your email and password.',
        'auth/too-many-requests': 'Too many attempts. Please try again later.',
        'auth/popup-closed-by-user': 'Sign-in popup was closed. Please try again.',
        'auth/cancelled-popup-request': 'Sign-in was cancelled.',
        'auth/popup-blocked': 'Sign-in popup was blocked. Please enable popups.',
        'auth/network-request-failed': 'Network error. Please check your connection.',
        'auth/invalid-verification-code': 'Invalid verification code. Please try again.',
        'auth/missing-verification-code': 'Please enter the verification code.',
        // Redirect-related errors (for native mobile)
        'auth/redirect-cancelled-by-user': 'Sign-in was cancelled. Please try again.',
        'auth/redirect-operation-pending': 'A sign-in operation is already in progress.',
        'auth/web-storage-unsupported': 'Your browser does not support web storage.',
        'auth/credential-already-in-use': 'This account is already linked to another user.',
        'auth/account-exists-with-different-credential': 'An account already exists with this email using a different sign-in method.',
      };

      const code = (err as { code?: string }).code || '';
      return errorMessages[code] || err.message;
    }
    return 'An unexpected error occurred. Please try again.';
  };

  const clearError = () => setError(null);

  // ---------------------------------------------------------------------------
  // Email/Password Authentication
  // ---------------------------------------------------------------------------

  const signUp = async (
    email: string,
    password: string,
    displayName: string,
    turnstileToken?: string,
  ) => {
    try {
      setError(null);
      setLoading(true);

      analyticsService.trackSignupStart('email');

      // Call backend /auth/register endpoint. The turnstile_token is verified
      // server-side; the backend skips verification when TURNSTILE_SECRET_KEY
      // is unset (dev/CI), so this remains optional from the client's POV.
      const response = await fetch(`${API_BASE}/api/v1/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          name: displayName,
          marketing_consent: false,
          turnstile_token: turnstileToken,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }

      const data = await response.json();

      // Sign in with custom token from backend
      const result = await signInWithCustomToken(auth, data.custom_token);

      // Send verification email
      if (result.user) {
        await sendEmailVerification(result.user);
      }

      analyticsService.trackSignupComplete('email', data.user_id);

    } catch (err) {
      const message = handleAuthError(err);
      setError(message);
      analyticsService.trackAuthError('email_signup', message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);

      analyticsService.trackLoginStart('email');

      const result = await signInWithEmailAndPassword(auth, email, password);

      analyticsService.trackLoginComplete('email', result.user.uid);

    } catch (err) {
      const message = handleAuthError(err);
      setError(message);
      analyticsService.trackAuthError('email_login', message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      const message = handleAuthError(err);
      setError(message);
      throw new Error(message);
    }
  };

  const resendVerificationEmail = async () => {
    try {
      setError(null);
      if (auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
      }
    } catch (err) {
      const message = handleAuthError(err);
      setError(message);
      throw new Error(message);
    }
  };

  // ---------------------------------------------------------------------------
  // OAuth Authentication
  // ---------------------------------------------------------------------------

  const signInWithOAuth = async (providerType: OAuthProviderType) => {
    try {
      setError(null);
      setLoading(true);

      analyticsService.trackLoginStart(providerType);

      const providers = {
        google: googleProvider,
        apple: appleProvider,
        // microsoft: microsoftProvider, // Removed
        // yahoo: yahooProvider, // Removed
      };

      const provider = providers[providerType];

      // Use popup sign-in for all platforms
      // Modern Capacitor WebViews handle popups well
      const result = await signInWithProvider(provider);

      if (result?.user) {
        analyticsService.trackLoginComplete(providerType, result.user.uid);
      }

    } catch (err) {
      const message = handleAuthError(err);
      setError(message);
      analyticsService.trackAuthError(`oauth_${providerType}`, message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Multi-Factor Authentication
  // ---------------------------------------------------------------------------

  const startMFAEnrollment = async (phoneNumber: string): Promise<string> => {
    try {
      setError(null);

      if (!auth.currentUser) {
        throw new Error('You must be signed in to enable MFA.');
      }

      // Initialize recaptcha if not already done
      let verifier = recaptchaVerifier;
      if (!verifier) {
        verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
          size: 'invisible',
        });
        setRecaptchaVerifier(verifier);
      }

      // Start phone verification for MFA enrollment
      const mfaSession = await multiFactor(auth.currentUser).getSession();
      const phoneInfoOptions = {
        phoneNumber,
        session: mfaSession,
      };

      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneAuthProvider.verifyPhoneNumber(
        phoneInfoOptions,
        verifier
      );

      setMfaState((prev) => ({
        ...prev,
        enrolling: true,
        verificationId,
      }));

      return verificationId;

    } catch (err) {
      const message = handleAuthError(err);
      setError(message);
      throw new Error(message);
    }
  };

  const completeMFAEnrollment = async (
    verificationCode: string,
    displayName: string = 'Phone'
  ) => {
    try {
      setError(null);

      if (!auth.currentUser || !mfaState.verificationId) {
        throw new Error('MFA enrollment session expired. Please try again.');
      }

      const cred = PhoneAuthProvider.credential(
        mfaState.verificationId,
        verificationCode
      );

      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

      await multiFactor(auth.currentUser).enroll(multiFactorAssertion, displayName);

      // Reset MFA state
      setMfaState({
        required: false,
        resolver: null,
        verificationId: null,
        enrolling: false,
      });

      // Refresh user to get updated MFA status
      const mfaUser = multiFactor(auth.currentUser);
      setUser((prev) =>
        prev
          ? {
              ...prev,
              mfaEnabled: true,
              enrolledFactors: mfaUser.enrolledFactors.map((f) => f.factorId),
            }
          : null
      );

    } catch (err) {
      const message = handleAuthError(err);
      setError(message);
      throw new Error(message);
    }
  };

  const verifyMFACode = async (verificationCode: string) => {
    try {
      setError(null);

      if (!mfaState.resolver) {
        throw new Error('MFA session expired. Please sign in again.');
      }

      // Get the first hint (phone factor)
      const hint = mfaState.resolver.hints[0];

      if (hint.factorId === PhoneMultiFactorGenerator.FACTOR_ID) {
        // Initialize recaptcha if not already done
        let verifier = recaptchaVerifier;
        if (!verifier) {
          verifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
            size: 'invisible',
          });
          setRecaptchaVerifier(verifier);
        }

        // If we don't have a verification ID, request one
        if (!mfaState.verificationId) {
          const phoneAuthProvider = new PhoneAuthProvider(auth);
          const verificationId = await phoneAuthProvider.verifyPhoneNumber(
            {
              multiFactorHint: hint,
              session: mfaState.resolver.session,
            },
            verifier
          );

          setMfaState((prev) => ({
            ...prev,
            verificationId,
          }));

          throw new Error('Verification code sent. Please enter the code.');
        }

        // Complete sign-in with the verification code
        const cred = PhoneAuthProvider.credential(
          mfaState.verificationId,
          verificationCode
        );

        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
        await mfaState.resolver.resolveSignIn(multiFactorAssertion);

        // Reset MFA state
        setMfaState({
          required: false,
          resolver: null,
          verificationId: null,
          enrolling: false,
        });
      }

    } catch (err) {
      const message = handleAuthError(err);
      setError(message);
      throw new Error(message);
    }
  };

  const cancelMFA = () => {
    setMfaState({
      required: false,
      resolver: null,
      verificationId: null,
      enrolling: false,
    });
    setError(null);
  };

  // ---------------------------------------------------------------------------
  // TOTP MFA Methods
  // ---------------------------------------------------------------------------

  const checkMFARequired = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);

      // Attempt Firebase sign-in first
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Check if MFA is enabled via backend
      const token = await getIdToken(userCredential.user);
      const response = await fetch(`${API_BASE}/api/v1/mfa/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.enabled) {
          // Sign out temporarily - user needs to complete MFA
          await signOut(auth);
          return true;
        }
      }

      return false;
    } catch (err: any) {
      // If MFA is required, Firebase will throw error
      if (err.code === 'auth/multi-factor-auth-required') {
        return true;
      }
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const verifyTOTPCode = async (email: string, password: string, code: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // First, sign in with email/password
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const token = await getIdToken(userCredential.user);

      // Verify TOTP code with backend
      const response = await fetch(`${API_BASE}/api/v1/mfa/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code }),
      });

      const data = await response.json();

      if (!response.ok || !data.verified) {
        throw new Error(data.message || 'Invalid verification code');
      }

      // MFA verified successfully - user is now signed in
      analyticsService.trackMfaVerification(true);
    } catch (err) {
      // SECURITY: Sign out if MFA verification fails to prevent bypass
      await signOut(auth);
      analyticsService.trackMfaVerification(false);
      const message = handleAuthError(err);
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Session Management
  // ---------------------------------------------------------------------------

  const signOutUser = async () => {
    try {
      setError(null);
      analyticsService.clearUser();
      await signOut(auth);
      setUser(null);
    } catch (err) {
      const message = handleAuthError(err);
      setError(message);
      throw new Error(message);
    }
  };

  const getAuthToken = async (): Promise<string | null> => {
    try {
      if (auth.currentUser) {
        return await getIdToken(auth.currentUser);
      }
      return null;
    } catch (err) {
      console.error('Error getting auth token:', err);
      return null;
    }
  };

  /**
   * Pull the authoritative user record from Firebase and republish `user`.
   *
   * Called by VerifyEmailPage while it waits for the verification link to be
   * clicked. `reload()` refetches the account server-side; after it resolves,
   * `auth.currentUser.emailVerified` reflects reality.
   *
   * NOTE the hazard this shares with the onAuthStateChanged handler above:
   * `mfaEnabled` is NOT a Firebase field. It is read from GET /auth/me and
   * merged into the snapshot afterwards. A naive full replace here would
   * overwrite it with the pessimistic default and silently tell a protected
   * user they are unprotected - the exact cross-identity staleness that the
   * review of PR #20 caught.
   */
  const refreshUser = async (): Promise<void> => {
    const current = auth.currentUser;
    if (!current) return;

    try {
      await reload(current);
    } catch (err) {
      console.error('Could not refresh the user from Firebase:', err);
      return;
    }

    // Spread `prev` FIRST so backend-sourced fields survive: `mfaEnabled`
    // comes from GET /auth/me, never from Firebase, and a full replace here
    // would reset it to the pessimistic default.
    //
    // The uid guard matters because reload() is async - the user can sign out
    // and back in as someone else while it is in flight, and without this the
    // resolved call would write account A's profile over account B's session.
    setUser((prev) => {
      if (!prev || prev.uid !== current.uid) return prev;
      return {
        ...prev,
        email: current.email,
        displayName: current.displayName,
        photoURL: current.photoURL,
        emailVerified: current.emailVerified,
        // Refreshed too, so an enrolment completed in this session is visible
        // without a re-login - which is exactly the Phase 1 flow.
        enrolledFactors: multiFactor(current).enrolledFactors.map((f) => f.factorId),
      };
    });

    // `mfaEnabled` is the one field reload() cannot tell us about: it is
    // `users.mfa_enabled` in Postgres, read through GET /auth/me, and
    // enrolling a TOTP authenticator does not create a Firebase second
    // factor. Preserving it above is what stops a clobber; re-reading it here
    // is what stops it going stale. Without this, a user who enrols mid
    // session is still offered "Enable Two-Factor Auth" until they sign out
    // and back in, because nothing re-runs onAuthStateChanged.
    const backendMfaEnabled = await fetchBackendMfaEnabled(current);
    if (backendMfaEnabled !== null) {
      // Guarded again: the fetch is a second async gap, and the identity can
      // change across it just as it can across reload().
      setUser((prev) =>
        prev && prev.uid === current.uid
          ? { ...prev, mfaEnabled: backendMfaEnabled }
          : prev,
      );
    }
  };

  // ---------------------------------------------------------------------------
  // Context Value
  // ---------------------------------------------------------------------------

  const value: AuthContextType = {
    user,
    loading,
    error,
    mfaState,
    signUp,
    signIn,
    resetPassword,
    resendVerificationEmail,
    signInWithOAuth,
    startMFAEnrollment,
    completeMFAEnrollment,
    verifyMFACode,
    cancelMFA,
    checkMFARequired,
    verifyTOTPCode,
    signOutUser,
    getAuthToken,
    refreshUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Invisible recaptcha container for MFA */}
      <div id="recaptcha-container" />
    </AuthContext.Provider>
  );
}

// =============================================================================
// Hook
// =============================================================================

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
